import {
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';

import {
  Preloader,
  H1,
  Separator,
  Paragraph,
  ICommonIconProps,
  Button,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { ESubscription, FeatureHOC, FeaturesContext } from 'features';
import { LocalizationContext } from 'localization';
import { CopySettingsModal } from 'modals';
import { SettingsContext } from 'settings';

import {
  BillingTab,
  IntegrationsTab,
  SecurityTab,
  ShellTab,
  CustomizationTab,
  SmartGamerTab,
  Banner,
} from './components';
import s from './Settings.module.scss';
import { getTabs } from './utils';

export const Settings: FC = () => {
  const { subscription } = useContext(FeaturesContext);
  const {
    state: { isInitialized, isLoading },
    actions: { loadSettings },
  } = useContext(SettingsContext);
  const { t } = useContext(LocalizationContext);

  const [bannerShow, setBannerShow] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [tab, setTab] = useState('billing');
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);

  const billingRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const securityRef = useRef<HTMLDivElement>(null);
  const customizationRef = useRef<HTMLDivElement>(null);
  const smartGamerRef = useRef<HTMLDivElement>(null);
  const integrationsRef = useRef<HTMLDivElement>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const tabs = useMemo<
    Array<{ Icon: FC<ICommonIconProps>; title: string; id: string }>
  >(() => getTabs(t), [t]);

  const tabRefs = useMemo(
    () => ({
      billing: billingRef,
      shell: shellRef,
      security: securityRef,
      customization: customizationRef,
      smartGamer: smartGamerRef,
      integrations: integrationsRef,
    }),
    []
  );

  useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollMobileMenuToActive = useCallback(
    (activeId: string) => {
      if (!isMobile || !mobileMenuRef.current) return;

      const menu = mobileMenuRef.current;
      const activeButton = menu.querySelector(
        `[data-id="${activeId}"]`
      ) as HTMLButtonElement;

      if (activeButton) {
        const menuRect = menu.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();

        const scrollLeft =
          buttonRect.left - menuRect.left + menu.scrollLeft - 32;

        menu.scrollTo({
          left: scrollLeft,
          behavior: 'smooth',
        });
      }
    },
    [isMobile, mobileMenuRef.current]
  );

  const handleMenuClick = useCallback(
    (id: string) => {
      if (!containerRef.current) return;

      if (isMobile) {
        scrollMobileMenuToActive(id);
      }

      setTab(id);
      const element = tabRefs[id as keyof typeof tabRefs]?.current;
      if (element && containerRef.current) {
        const container = containerRef.current;
        const elementPosition = element.offsetTop - container.offsetTop;
        let offsetPosition = elementPosition - 32;

        if (isMobile) {
          offsetPosition = elementPosition + 132;
        }

        container.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    },
    [tabRefs, containerRef, isMobile, scrollMobileMenuToActive]
  );

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
    if (isAtBottom) {
      setTab('integrations');
      return;
    }

    let currentActiveTab = 'billing';
    let minDistance = Infinity;

    tabs.forEach(({ id }) => {
      const element = tabRefs[id as keyof typeof tabRefs]?.current;
      if (!element) return;

      const containerTop = container.getBoundingClientRect().top;
      const elementTop = element.getBoundingClientRect().top;

      const distanceFromTop = elementTop - containerTop - (isMobile ? 100 : 82);

      if (distanceFromTop <= 32 && distanceFromTop >= -element.offsetHeight) {
        const distance = Math.abs(distanceFromTop);
        if (distance < minDistance) {
          minDistance = distance;
          currentActiveTab = id;
        }
      }
    });

    setTab(currentActiveTab);

    if (isMobile) {
      scrollMobileMenuToActive(currentActiveTab);
    }
  }, [tabs, tabRefs, isMobile, scrollMobileMenuToActive]);

  useEffect(() => {
    if (!isLoading) {
      loadSettings();
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, containerRef.current]);

  return (
    <Preloader loading={!isInitialized}>
      {subscription === ESubscription.FREE && bannerShow && (
        <Banner setBannerShow={setBannerShow} />
      )}
      {isModalOpen && <CopySettingsModal close={() => setIsModalOpen(false)} />}
      <div className={cn(s.menu, s.mobile_menu)} ref={mobileMenuRef}>
        {tabs.map(({ id, Icon, title }) => (
          <button
            className={cn(s.menu__btn, tab === id && s.menu__btn_active)}
            onClick={() => handleMenuClick(id)}
            key={`mobile-button-${id}`}
            data-id={id}
          >
            <Icon size="m" />
            <Paragraph bold>{title}</Paragraph>
          </button>
        ))}
      </div>
      <div className={s.container} ref={containerRef}>
        <div className={s.container__left}>
          {tabs.map(({ id, Icon, title }) => (
            <div
              id={id}
              className={s.setting}
              key={`content-${id}`}
              ref={tabRefs[id as keyof typeof tabRefs]}
            >
              <div className={s.title}>
                <Icon size="m" />
                <H1>{title}</H1>
              </div>

              {id === 'billing' && <BillingTab />}
              {id === 'shell' && <ShellTab />}
              {id === 'security' && <SecurityTab />}
              {id === 'customization' && <CustomizationTab />}
              {id === 'smartGamer' && <SmartGamerTab />}
              {id === 'integrations' && <IntegrationsTab />}
              <Separator />
            </div>
          ))}

          <FeatureHOC alias="copy_settings" strategy="violetReplace">
            <Button
              fullWidth
              variant="secondary"
              onClick={() => setIsModalOpen(true)}
              className={s.copy_setting__button}
            >
              {t('settings.copy.button')}
            </Button>
          </FeatureHOC>
        </div>

        <div className={s.menu}>
          {tabs.map(({ id, Icon, title }) => (
            <button
              className={cn(s.menu__btn, tab === id && s.menu__btn_active)}
              onClick={() => handleMenuClick(id)}
              key={`button-${id}`}
            >
              <Icon size="m" />
              <Paragraph bold>{title}</Paragraph>
            </button>
          ))}
        </div>
      </div>
    </Preloader>
  );
};
